import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  endorsementCatalog,
  policies,
  type EndorsementType,
} from '@/shared/api/resources';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DetailError, DetailLoading } from '@/shared/ui/DetailLayout';
import { PageHeader } from '@/shared/ui/PageHeader';

import { numeroDaApolice, tomadorDaApolice, vigenciaDe } from '../portfolio/apolice';

import { caminhoDoAssistente, codigoDeEndosso } from './endosso';

/**
 * Tela "Selecionar tipo de endosso" (`/selecionar-tipo-endosso/:policyUniqueId` do
 * OnPoint, END-001; aqui `/apolices/:policyId/endosso`).
 *
 * Os tipos habilitados com a descricao, os subtipos ativos do tipo escolhido e a
 * elegibilidade da apolice conferida no servidor antes de abrir o assistente. O
 * rascunho que a pessoa ja abriu para o mesmo subtipo e oferecido para continuar.
 */
export function EndorsementTypePage() {
  const { policyId = '' } = useParams();
  const [parametros] = useSearchParams();
  const navigate = useNavigate();
  const [escolhido, setEscolhido] = useState<string | null>(
    codigoDeEndosso(parametros.get('tipo') ?? undefined),
  );
  const [subtipo, setSubtipo] = useState('');

  const apolice = useQuery({
    queryKey: ['policy', policyId],
    queryFn: ({ signal }) => policies.detail(policyId, signal),
    enabled: policyId !== '',
  });
  const tipos = useQuery({
    queryKey: ['endorsement-types', 'habilitados'],
    queryFn: ({ signal }) => endorsementCatalog.types(true, signal),
    retry: false,
  });
  const tipo: EndorsementType | undefined = (tipos.data ?? []).find(
    (item) => item.code === escolhido,
  );
  const subtipos = useQuery({
    queryKey: ['endorsement-subtypes', 'ativos', tipo?.id],
    queryFn: ({ signal }) => endorsementCatalog.subtypes(tipo?.id, false, signal),
    enabled: tipo !== undefined,
    retry: false,
  });
  const elegivel = useQuery({
    queryKey: ['endorsement-eligibility', policyId, tipo?.code],
    queryFn: ({ signal }) =>
      tipo
        ? endorsementCatalog.eligibility(policyId, tipo.code, signal)
        : Promise.reject(new Error('Tipo de endosso invalido ou nao implementado.')),
    enabled: tipo !== undefined && policyId !== '',
    retry: false,
  });

  if (apolice.isPending) return <DetailLoading />;
  if (apolice.isError) {
    return (
      <DetailError
        title="Selecionar tipo de endosso"
        alertTitle="Erro ao buscar apolice"
        message={apolice.error.message}
        backLabel="Lista de apolices"
        onBack={() => void navigate('/apolices')}
      />
    );
  }

  const policy = apolice.data;
  const numero = numeroDaApolice(policy);
  const rascunho = elegivel.data?.drafts.find(
    (item) => item.endorsement_subtype_id === subtipo,
  );

  return (
    <div className="flex flex-col gap-5">
      <nav aria-label="Trilha" className="text-xs text-ink-500">
        <Link
          to="/apolices"
          className="text-brand-600 underline-offset-2 hover:underline"
        >
          Lista de apolices
        </Link>{' '}
        /{' '}
        <Link
          to={`/apolices/${policy.id}`}
          className="text-brand-600 underline-offset-2 hover:underline"
        >
          Apolice {numero}
        </Link>{' '}
        / Endosso
      </nav>

      <PageHeader
        title="Selecionar tipo de endosso"
        description={`Apolice ${numero} - ${tomadorDaApolice(policy).nome} - ${formatMoney(
          policy.insured_amount,
        )} - ${vigenciaDe(policy.coverage_start, policy.coverage_end)}`}
      />

      {tipos.isError ? (
        <Alert tone="danger" title="Erro ao carregar permissoes">
          {tipos.error.message}
        </Alert>
      ) : null}
      {tipos.isSuccess && tipos.data.length === 0 ? (
        <Alert tone="warning" title="Atencao!">
          Desculpe, mas nao e possivel solicitar um endosso pois nao existe nenhum tipo
          habilitado.
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title="Selecione o tipo de endosso que deseja iniciar"
          description="Os tipos habilitados para a empresa."
        />
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-3">
            {(tipos.data ?? []).map((item) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={item.code === escolhido}
                onClick={() => {
                  setEscolhido(item.code);
                  setSubtipo('');
                }}
                className={cn(
                  'flex flex-col items-start gap-1 rounded-md border px-4 py-3 text-left',
                  item.code === escolhido
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-line hover:border-brand-500',
                )}
              >
                <span className="text-sm font-medium text-ink-900">{item.name}</span>
                <span className="text-xs text-ink-600">{item.description}</span>
              </button>
            ))}
          </div>
        </CardBody>
      </Card>

      {tipo ? (
        <Card>
          <CardHeader title="Subtipo de endosso" description={tipo.name} />
          <CardBody className="flex flex-col gap-3">
            {subtipos.isPending ? (
              <p className="text-sm text-ink-500">Carregando motivos</p>
            ) : null}
            {subtipos.isSuccess && subtipos.data.length === 0 ? (
              <Alert tone="warning" title="Nenhum subtipo ativo">
                O tipo nao tem subtipo ativo configurado. A configuracao fica em
                Endossos, Configuracao.
              </Alert>
            ) : null}
            <fieldset className="flex flex-col gap-2">
              <legend className="sr-only">Subtipo de endosso</legend>
              {(subtipos.data ?? []).map((item) => (
                <label
                  key={item.id}
                  className="flex cursor-pointer items-start gap-2 rounded-md border border-line px-3 py-2 text-sm"
                >
                  <input
                    type="radio"
                    name="subtipo"
                    value={item.id}
                    checked={subtipo === item.id}
                    onChange={() => setSubtipo(item.id)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium text-ink-900">{item.name}</span>
                    {item.description ? (
                      <span className="block text-xs text-ink-600">
                        {item.description}
                      </span>
                    ) : null}
                  </span>
                </label>
              ))}
            </fieldset>

            {elegivel.isError ? (
              <Alert tone="danger" title="Erro ao validar o endosso">
                {elegivel.error.message}
              </Alert>
            ) : null}
            {elegivel.data && !elegivel.data.eligible ? (
              <Alert tone="warning" title="A apolice nao aceita este endosso agora">
                <ul className="list-disc pl-4">
                  {elegivel.data.reasons.map((motivo) => (
                    <li key={motivo}>{motivo}</li>
                  ))}
                </ul>
              </Alert>
            ) : null}
            {elegivel.data?.calculation_pending ? (
              <Alert tone="info" title="Calculo pendente">
                Este tipo movimenta premio ou restitui, e o calculo ainda nao foi
                homologado. O assistente guarda os campos no rascunho; o envio fica para
                quando a regra entrar.
              </Alert>
            ) : null}

            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => void navigate(`/apolices/${policy.id}`)}
              >
                Cancelar
              </Button>
              <Button
                disabled={subtipo === '' || !elegivel.data?.eligible}
                onClick={() =>
                  void navigate(
                    caminhoDoAssistente(policy.id, tipo.code, subtipo, rascunho?.id),
                  )
                }
              >
                {rascunho
                  ? `Continuar o rascunho do endosso ${rascunho.number}`
                  : 'Iniciar'}
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
